import React from 'react'
import { Avatar, Button, Card, CardContent, CardHeader, Input } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  addCommunityModerator,
  listCommunityModerators,
  removeCommunityModerator,
  searchMasterUsers,
  type CommunityModeratorUser,
  type MasterProfileContact,
} from '../services/communityModeratorsService'

export default function CommunityModeratorsSettings() {
  const { add: toast } = useToast()
  const [loadingModerators, setLoadingModerators] = React.useState(true)
  const [moderators, setModerators] = React.useState<CommunityModeratorUser[]>([])
  const [query, setQuery] = React.useState('')
  const [searching, setSearching] = React.useState(false)
  const [searchError, setSearchError] = React.useState<string | null>(null)
  const [searchResults, setSearchResults] = React.useState<MasterProfileContact[]>([])
  const [mutatingUserId, setMutatingUserId] = React.useState<string | null>(null)

  const moderatorIds = React.useMemo(
    () => new Set(moderators.map((m) => m.id)),
    [moderators],
  )

  const loadModerators = React.useCallback(async () => {
    setLoadingModerators(true)
    try {
      const items = await listCommunityModerators()
      setModerators(items)
    } catch (e: any) {
      toast({
        title: 'Moderadores da Comunidade',
        description: e?.message || 'Falha ao carregar a lista de moderadores',
        variant: 'error',
      })
    } finally {
      setLoadingModerators(false)
    }
  }, [toast])

  React.useEffect(() => {
    loadModerators()
  }, [loadModerators])

  React.useEffect(() => {
    const q = query.trim()
    if (!q) {
      setSearching(false)
      setSearchError(null)
      setSearchResults([])
      return
    }

    let active = true
    setSearching(true)
    setSearchError(null)

    const t = setTimeout(async () => {
      try {
        const results = await searchMasterUsers({ query: q, limit: 8 })
        if (!active) return
        setSearchResults(results)
      } catch (e: any) {
        if (!active) return
        setSearchError(e?.message || 'Falha ao buscar usuários')
        setSearchResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 350)

    return () => {
      active = false
      clearTimeout(t)
    }
  }, [query])

  async function handleAdd(userId: string) {
    if (mutatingUserId) return
    setMutatingUserId(userId)
    try {
      await addCommunityModerator(userId)
      toast({
        title: 'Moderadores da Comunidade',
        description: 'Usuário adicionado como moderador com sucesso.',
        variant: 'success',
        soft: true,
      })
      await loadModerators()
    } catch (e: any) {
      toast({
        title: 'Moderadores da Comunidade',
        description: e?.message || 'Falha ao adicionar moderador',
        variant: 'error',
      })
    } finally {
      setMutatingUserId(null)
    }
  }

  async function handleRemove(userId: string) {
    if (mutatingUserId) return
    setMutatingUserId(userId)
    try {
      await removeCommunityModerator(userId)
      toast({
        title: 'Moderadores da Comunidade',
        description: 'Permissão removida com sucesso.',
        variant: 'success',
        soft: true,
      })
      setModerators((prev) => prev.filter((m) => m.id !== userId))
    } catch (e: any) {
      toast({
        title: 'Moderadores da Comunidade',
        description: e?.message || 'Falha ao remover moderador',
        variant: 'error',
      })
    } finally {
      setMutatingUserId(null)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Moderadores (excluir comentários)</CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">O que o moderador pode fazer</div>
          <p className="text-xs text-foreground/60">
            Usuários cadastrados como <span className="font-medium">Moderador</span> podem <span className="font-medium">excluir</span>{' '}
            comentários de outros usuários em todos os módulos que possuem Comunidade (Oportunidades, Análises, etc). Somente usuários
            adicionados aqui terão esse recurso.
          </p>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Adicionar moderador</div>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar usuário por nome ou e-mail..."
          />

          {searchError && (
            <div className="text-xs text-destructive">
              {searchError}
            </div>
          )}

          {query.trim().length > 0 && (
            <div className="mt-2 space-y-2">
              {searching && (
                <div className="text-xs text-foreground/60">
                  Buscando...
                </div>
              )}

              {!searching && !searchError && searchResults.length === 0 && (
                <div className="text-xs text-foreground/60">
                  Nenhum usuário encontrado.
                </div>
              )}

              {!searching && searchResults.map((u) => {
                const id = String(u.id)
                const isAlreadyModerator = moderatorIds.has(id)
                const isMaster = String(u.role || '').trim() === 'master'
                const disabled = !!mutatingUserId || isAlreadyModerator || isMaster

                return (
                  <div
                    key={id}
                    className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar
                        src={u.avatar_url || undefined}
                        alt={String(u.full_name || '').trim() || 'Usuário'}
                        className="h-7 w-7"
                        fallback={(String(u.full_name || '').trim()?.[0] || 'U')}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {String(u.full_name || '').trim() || '(Sem nome)'}
                        </div>
                        <div className="truncate text-xs text-foreground/60">
                          {String(u.email || '').trim() || '—'}
                          {u.role ? ` • ${u.role}` : ''}
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      disabled={disabled}
                      onClick={() => handleAdd(id)}
                    >
                      {isMaster ? 'Master (já possui)' : isAlreadyModerator ? 'Já é moderador' : 'Adicionar'}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Moderadores cadastrados</div>

          {loadingModerators && (
            <div className="text-xs text-foreground/60">
              Carregando...
            </div>
          )}

          {!loadingModerators && moderators.length === 0 && (
            <div className="text-xs text-foreground/60">
              Nenhum moderador cadastrado ainda.
            </div>
          )}

          {!loadingModerators && moderators.length > 0 && (
            <div className="space-y-2">
              {moderators.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar
                      src={m.avatarUrl || undefined}
                      alt={m.fullName || 'Usuário'}
                      className="h-7 w-7"
                      fallback={(m.fullName?.[0] || 'U')}
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {m.fullName || '(Sem nome)'}
                      </div>
                      <div className="truncate text-xs text-foreground/60">
                        {m.email || '—'}
                        {m.role ? ` • ${m.role}` : ''}
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    disabled={!!mutatingUserId}
                    onClick={() => handleRemove(m.id)}
                  >
                    Remover
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

